/**
 * Mock CMS content, shaped by the `@interfaces` CMS types.
 *
 * These mocks let the frontend render real layouts and be unit-tested before
 * the FT backend implements the live CMS endpoints (contract-first). They are
 * served by `cmsApi` when `CMS_USE_MOCKS` is on (see `cmsApi.ts`). Only
 * `status: "published"` records are returned by public reads; drafts are
 * returned only in preview mode.
 */
import {
    CmsFlavor,
    CmsHomepage,
    CmsMenu,
    CmsMenuLocation,
    CmsPage,
    CmsPost,
    CmsPostList,
    CmsPostSummary,
    CmsTemplate,
} from "@interfaces";

const nowIso = "2026-01-01T00:00:00.000Z";

export const MOCK_TEMPLATES: CmsTemplate[] = [
    {
        key: "standard",
        name: "Standard Page",
        allowedSections: ["hero", "richText", "imageText", "gallery", "cardGrid", "faq", "cta", "html", "spacer"],
        description: "General purpose page with a hero and stacked content blocks.",
    },
    {
        key: "landing",
        name: "Landing Page",
        allowedSections: ["hero", "cardGrid", "cta", "productCarousel", "faq"],
        description: "Marketing landing page emphasising CTAs and product carousels.",
    },
    {
        key: "article",
        name: "Article / Blog Post",
        allowedSections: ["richText", "imageText", "gallery", "html", "cta"],
        description: "Long-form editorial layout used by blog posts.",
    },
];

const aboutPage: CmsPage = {
    id: "page_about",
    slug: "about-us",
    title: "About Us",
    flavor: "all",
    template: "standard",
    status: "published",
    publishedAt: nowIso,
    updatedAt: nowIso,
    seo: {
        title: "About Us | Travel & Live Your Best Life",
        metaDescription:
            "Learn about our mission to make independent travel effortless, and the team behind the trips.",
        structuredDataType: "AboutPage",
        ogImage: "/images/website/banner/banner.png",
    },
    sections: [
        {
            id: "s1",
            type: "hero",
            data: {
                heading: "About Us",
                subheading: "Travel and live your best life",
                imageUrl: "/images/website/banner/banner.png",
                imageAlt: "Scenic travel banner",
                align: "center",
            },
        },
        {
            id: "s2",
            type: "richText",
            data: {
                body: "<p>We help travellers discover, plan and book unforgettable trips. Our platform brings together tours, accommodation and transport from trusted operators worldwide.</p>",
            },
        },
        {
            id: "s3",
            type: "cardGrid",
            data: {
                heading: "Why travel with us",
                items: [
                    { id: "c1", title: "Handpicked trips", body: "Curated experiences from vetted operators." },
                    { id: "c2", title: "Flexible booking", body: "Guest checkout and easy account creation." },
                    { id: "c3", title: "Local expertise", body: "On-the-ground knowledge for every destination." },
                ],
            },
        },
        {
            id: "s4",
            type: "cta",
            data: {
                heading: "Ready to explore?",
                ctaLabel: "Browse trips",
                ctaHref: "/search-result",
            },
        },
    ],
};

const contactPage: CmsPage = {
    id: "page_contact",
    slug: "contact",
    title: "Contact",
    flavor: "all",
    template: "standard",
    status: "published",
    publishedAt: nowIso,
    updatedAt: nowIso,
    seo: {
        title: "Contact Us",
        metaDescription: "Get in touch with our travel team for help with bookings and enquiries.",
        structuredDataType: "ContactPage",
    },
    sections: [
        { id: "s1", type: "hero", data: { heading: "Contact Us", align: "center" } },
        {
            id: "s2",
            type: "richText",
            data: {
                body: "<p>Have a question? Email us and our team will get back to you within one business day.</p>",
            },
        },
    ],
};

const draftPreviewPage: CmsPage = {
    id: "page_draft",
    slug: "coming-soon",
    title: "Coming Soon (Draft)",
    flavor: "all",
    template: "landing",
    status: "draft",
    publishedAt: null,
    updatedAt: nowIso,
    seo: {
        title: "Coming Soon",
        metaDescription: "A preview-only draft page.",
        noindex: true,
    },
    sections: [
        { id: "s1", type: "hero", data: { heading: "Something new is coming", align: "center" } },
    ],
};

const MOCK_PAGES: CmsPage[] = [aboutPage, contactPage, draftPreviewPage];

const post1: CmsPost = {
    id: "post_top_10",
    slug: "top-10-outback-adventures",
    title: "Top 10 Outback Adventures",
    excerpt: "From Uluru sunsets to Karijini gorges — our favourite outback trips this year.",
    coverImage: "https://cms.websitetravel.com/upload/lg/590a752b9fd48_10_par4australianorthern-territoryhappyjumpingwomanulurusunset.jpg",
    author: { id: "a1", name: "Travel Desk", bio: "Our in-house travel editors." },
    publishedAt: "2026-02-10T00:00:00.000Z",
    status: "published",
    tags: ["australia", "outback", "adventure"],
    readingMinutes: 6,
    flavor: "all",
    seo: {
        title: "Top 10 Outback Adventures",
        metaDescription: "Our editors pick the top 10 outback adventures for independent travellers.",
        structuredDataType: "BlogPosting",
        ogImage: "https://cms.websitetravel.com/upload/lg/590a752b9fd48_10_par4australianorthern-territoryhappyjumpingwomanulurusunset.jpg",
    },
    sections: [
        {
            id: "s1",
            type: "richText",
            data: {
                body: "<p>The Australian outback is vast, ancient and endlessly rewarding. Here are ten trips worth planning your year around.</p>",
            },
        },
        {
            id: "s2",
            type: "imageText",
            data: {
                heading: "1. Uluru at sunset",
                body: "<p>Watch the rock change colour as the sun goes down over the Red Centre.</p>",
                imageUrl: "https://cms.websitetravel.com/upload/lg/590a752b9fd48_10_par4australianorthern-territoryhappyjumpingwomanulurusunset.jpg",
                imagePosition: "left",
            },
        },
    ],
};

const post2: CmsPost = {
    id: "post_packing",
    slug: "packing-guide-for-long-trips",
    title: "The Ultimate Packing Guide for Long Trips",
    excerpt: "Pack smarter, not heavier — a practical checklist for multi-week travel.",
    coverImage: "https://cms.websitetravel.com/upload/lg/590a75954bc36_10_pbp10australiawestern-australiakarijinigorge.jpg",
    author: { id: "a1", name: "Travel Desk" },
    publishedAt: "2026-03-01T00:00:00.000Z",
    status: "published",
    tags: ["tips", "packing"],
    readingMinutes: 4,
    flavor: "all",
    seo: {
        title: "The Ultimate Packing Guide for Long Trips",
        metaDescription: "A practical, no-nonsense packing checklist for multi-week adventures.",
        structuredDataType: "BlogPosting",
    },
    sections: [
        {
            id: "s1",
            type: "richText",
            data: {
                body: "<p>Long trips reward travellers who pack light and plan ahead. Start with this checklist.</p>",
            },
        },
    ],
};

const draftPost: CmsPost = {
    id: "post_draft",
    slug: "unpublished-draft-post",
    title: "Unpublished Draft Post",
    excerpt: "This post is a draft and should only appear in preview mode.",
    author: { id: "a1", name: "Travel Desk" },
    publishedAt: null,
    status: "draft",
    tags: ["draft"],
    flavor: "all",
    seo: { title: "Unpublished Draft Post", metaDescription: "Draft.", noindex: true },
    sections: [{ id: "s1", type: "richText", data: { body: "<p>Draft body.</p>" } }],
};

const MOCK_POSTS: CmsPost[] = [post1, post2, draftPost];

const homepage: CmsHomepage = {
    flavor: "all",
    updatedAt: nowIso,
    seo: {
        title: "Travel & Live Your Best Life",
        metaDescription:
            "Discover, plan and book tours, accommodation and transport for your next adventure.",
        structuredDataType: "WebPage",
        ogImage: "/images/website/banner/banner.png",
    },
    hero: {
        id: "home_hero",
        type: "hero",
        data: {
            heading: "Travel & live your best life",
            imageUrl: "/images/website/banner/banner.png",
            imageAlt: "Travel Desktop Banner",
            align: "center",
        },
    },
    sections: [
        {
            id: "home_promo",
            type: "cta",
            data: {
                heading: "Plan a multi-day trip",
                subheading: "Build a day-by-day itinerary with our Trip Planner.",
                ctaLabel: "Open Trip Planner",
                ctaHref: "/trip-planner",
            },
        },
    ],
};

const summarise = (p: CmsPost): CmsPostSummary => ({
    id: p.id,
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    coverImage: p.coverImage,
    author: p.author,
    publishedAt: p.publishedAt,
    status: p.status,
    tags: p.tags,
    readingMinutes: p.readingMinutes,
    flavor: p.flavor,
});

const matchesFlavor = (
    itemFlavor: CmsFlavor | "all" | undefined,
    flavor: CmsFlavor | undefined,
): boolean => {
    if (!itemFlavor || itemFlavor === "all") return true;
    if (!flavor) return true;
    return itemFlavor === flavor;
};

const isVisible = (status: string, preview?: boolean) => preview === true || status === "published";

export const cmsMocks = {
    getTemplates: (): CmsTemplate[] => MOCK_TEMPLATES,

    getHomepage: (flavor?: CmsFlavor): CmsHomepage => ({
        ...homepage,
        flavor: flavor ?? "all",
    }),

    getPageBySlug: (
        slug: string,
        opts?: { preview?: boolean; flavor?: CmsFlavor },
    ): CmsPage | null => {
        const page = MOCK_PAGES.find(
            (p) => p.slug === slug && matchesFlavor(p.flavor, opts?.flavor),
        );
        if (!page) return null;
        if (!isVisible(page.status, opts?.preview)) return null;
        return page;
    },

    listPosts: (
        opts?: { page?: number; perPage?: number; tag?: string; preview?: boolean; flavor?: CmsFlavor },
    ): CmsPostList => {
        const page = Math.max(1, opts?.page ?? 1);
        const perPage = Math.max(1, opts?.perPage ?? 10);
        const filtered = MOCK_POSTS.filter(
            (p) =>
                isVisible(p.status, opts?.preview) &&
                matchesFlavor(p.flavor, opts?.flavor) &&
                (!opts?.tag || (p.tags ?? []).includes(opts.tag)),
        ).sort((a, b) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""));
        const total = filtered.length;
        const start = (page - 1) * perPage;
        const posts = filtered.slice(start, start + perPage).map(summarise);
        return {
            posts,
            pagination: {
                page,
                perPage,
                total,
                totalPages: Math.max(1, Math.ceil(total / perPage)),
            },
        };
    },

    getPostBySlug: (
        slug: string,
        opts?: { preview?: boolean; flavor?: CmsFlavor },
    ): CmsPost | null => {
        const post = MOCK_POSTS.find(
            (p) => p.slug === slug && matchesFlavor(p.flavor, opts?.flavor),
        );
        if (!post) return null;
        if (!isVisible(post.status, opts?.preview)) return null;
        return post;
    },

    getMenu: (location: CmsMenuLocation, flavor?: CmsFlavor): CmsMenu => {
        const headerItems = [
            { id: "m_home", label: "Home", href: "/", order: 1, visibleTo: "all" as const },
            { id: "m_search", label: "Explore", href: "/search-result", order: 2, visibleTo: "all" as const },
            { id: "m_trip", label: "Trip Planner", href: "/trip-planner", order: 3, visibleTo: "all" as const },
            { id: "m_blog", label: "Blog", href: "/blog", order: 4, visibleTo: "all" as const },
            { id: "m_about", label: "About", href: "/about-us", order: 5, visibleTo: "all" as const },
        ];
        const footerItems = [
            { id: "f_home", label: "Home", href: "/", order: 1, visibleTo: "all" as const },
            { id: "f_about", label: "About Us", href: "/about-us", order: 2, visibleTo: "all" as const },
            { id: "f_blog", label: "Blog", href: "/blog", order: 3, visibleTo: "all" as const },
            { id: "f_contact", label: "Contact", href: "/contact", order: 4, visibleTo: "all" as const },
            { id: "f_privacy", label: "Privacy Policy", href: "/privacy-policy", order: 5, visibleTo: "all" as const },
        ];
        return {
            location,
            flavor: flavor ?? "all",
            items: location === "header" ? headerItems : footerItems,
        };
    },
};
